import React, { useState, useEffect } from 'react';
import { Clock, Calendar, CheckCircle } from 'lucide-react';

export interface ScheduleRestrictions {
  type: 'always' | 'custom';
  time_restrictions?: {
    start_time: string;
    end_time: string;
  };
  day_restrictions?: {
    days_of_week: number[];
    allowed_days: string[];
  };
  date_range?: {
    start_date: string;
    end_date: string;
  };
}

interface ScheduleInputProps {
  value?: ScheduleRestrictions;
  onChange: (schedule: ScheduleRestrictions) => void;
  disabled?: boolean;
  darkMode?: boolean;
  hideTypeSelector?: boolean;
}

const DAYS_OF_WEEK = [
  { label: 'Dom', value: 0, name: 'sunday' },
  { label: 'Seg', value: 1, name: 'monday' },
  { label: 'Ter', value: 2, name: 'tuesday' },
  { label: 'Qua', value: 3, name: 'wednesday' },
  { label: 'Qui', value: 4, name: 'thursday' },
  { label: 'Sex', value: 5, name: 'friday' },
  { label: 'Sáb', value: 6, name: 'saturday' },
];

export const ScheduleInput: React.FC<ScheduleInputProps> = ({
  value,
  onChange,
  disabled = false,
  darkMode = false,
  hideTypeSelector = false,
}) => {
  const [scheduleType, setScheduleType] = useState<'always' | 'custom'>(
    value?.type || 'custom'
  );
  const [timeStart, setTimeStart] = useState(
    value?.time_restrictions?.start_time || '08:00'
  );
  const [timeEnd, setTimeEnd] = useState(
    value?.time_restrictions?.end_time || '18:00'
  );
  const [selectedDays, setSelectedDays] = useState<number[]>(
    value?.day_restrictions?.days_of_week || [1, 2, 3, 4, 5]
  );
  const [dateStart, setDateStart] = useState(
    value?.date_range?.start_date || ''
  );
  const [dateEnd, setDateEnd] = useState(
    value?.date_range?.end_date || ''
  );

  useEffect(() => {
    const schedule: ScheduleRestrictions = {
      type: scheduleType,
      ...(scheduleType === 'custom' && {
        time_restrictions: {
          start_time: timeStart,
          end_time: timeEnd
        },
        day_restrictions: {
          days_of_week: selectedDays.sort(),
          allowed_days: selectedDays.map(day =>
            DAYS_OF_WEEK.find(d => d.value === day)?.name || ''
          ).filter(Boolean)
        },
        ...(dateStart || dateEnd ? {
          date_range: {
            start_date: dateStart,
            end_date: dateEnd
          }
        } : {})
      })
    };
    onChange(schedule);
  }, [scheduleType, timeStart, timeEnd, selectedDays, dateStart, dateEnd]);

  const toggleDay = (day: number) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const selectWeekdays = () => {
    setSelectedDays([1, 2, 3, 4, 5]); // Monday to Friday
  };

  const selectWeekend = () => {
    setSelectedDays([0, 6]); // Sunday and Saturday
  };

  const selectAllDays = () => {
    setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
  };

  return (
    <div className="space-y-4">
      {/* Schedule Type Selection */}
      {!hideTypeSelector && <div>
        <label className={`block text-sm font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          Tipo de Horário
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label
            className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
              scheduleType === 'custom'
                ? darkMode
                  ? 'border-green-500 bg-green-500/20 shadow-lg ring-2 ring-green-400/50'
                  : 'border-green-500 bg-green-50 shadow-lg ring-2 ring-green-200'
                : darkMode
                  ? 'border-gray-600 bg-gray-800 hover:border-gray-500 hover:shadow-md'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input
              type="radio"
              value="custom"
              checked={scheduleType === 'custom'}
              onChange={(e) => setScheduleType(e.target.value as 'custom')}
              disabled={disabled}
              className="sr-only"
            />
            <Calendar
              className={`h-5 w-5 mr-2 transition-colors ${
                scheduleType === 'custom'
                  ? darkMode ? 'text-green-400' : 'text-green-600'
                  : darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
            />
            <span
              className={`text-sm font-semibold transition-colors ${
                scheduleType === 'custom'
                  ? darkMode ? 'text-green-300' : 'text-green-700'
                  : darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Horário Personalizado
            </span>
          </label>

          <label
            className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
              scheduleType === 'always'
                ? darkMode
                  ? 'border-green-500 bg-green-500/20 shadow-lg ring-2 ring-green-400/50'
                  : 'border-green-500 bg-green-50 shadow-lg ring-2 ring-green-200'
                : darkMode
                  ? 'border-gray-600 bg-gray-800 hover:border-gray-500 hover:shadow-md'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input
              type="radio"
              value="always"
              checked={scheduleType === 'always'}
              onChange={(e) => setScheduleType(e.target.value as 'always')}
              disabled={disabled}
              className="sr-only"
            />
            <CheckCircle
              className={`h-5 w-5 mr-2 transition-colors ${
                scheduleType === 'always'
                  ? darkMode ? 'text-green-400' : 'text-green-600'
                  : darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
            />
            <span
              className={`text-sm font-semibold transition-colors ${
                scheduleType === 'always'
                  ? darkMode ? 'text-green-300' : 'text-green-700'
                  : darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Sempre Permitido
            </span>
          </label>
        </div>
      </div>}

      {/* Custom Schedule Options */}
      {(hideTypeSelector || scheduleType === 'custom') && (
        <>
          {/* Time Range */}
          <div className={`rounded-lg p-4 space-y-4 ${
            darkMode ? 'bg-gray-800' : 'bg-gray-50'
          }`}>
            <div className={`flex items-center text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <Clock className={`h-4 w-4 mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              Horário de Acesso
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Horário Início
                </label>
                <input
                  type="time"
                  value={timeStart}
                  onChange={(e) => setTimeStart(e.target.value)}
                  disabled={disabled}
                  className={`block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white disabled:bg-gray-900 disabled:cursor-not-allowed'
                      : 'border border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Horário Fim
                </label>
                <input
                  type="time"
                  value={timeEnd}
                  onChange={(e) => setTimeEnd(e.target.value)}
                  disabled={disabled}
                  className={`block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white disabled:bg-gray-900 disabled:cursor-not-allowed'
                      : 'border border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Days of Week */}
          <div className={`rounded-lg p-4 space-y-3 ${
            darkMode ? 'bg-gray-800' : 'bg-gray-50'
          }`}>
            <div className="flex items-center justify-between">
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Dias da Semana
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectWeekdays}
                  disabled={disabled}
                  className={`text-xs px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode
                      ? 'bg-primary-600/20 text-primary-400 hover:bg-primary-600/30'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  Seg-Sex
                </button>
                <button
                  type="button"
                  onClick={selectWeekend}
                  disabled={disabled}
                  className={`text-xs px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode
                      ? 'bg-primary-600/20 text-primary-400 hover:bg-primary-600/30'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  Fim de semana
                </button>
                <button
                  type="button"
                  onClick={selectAllDays}
                  disabled={disabled}
                  className={`text-xs px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode
                      ? 'bg-primary-600/20 text-primary-400 hover:bg-primary-600/30'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  Todos
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  disabled={disabled}
                  className={`flex-1 py-2 px-1 rounded-md text-sm font-semibold transition-colors ${
                    selectedDays.includes(day.value)
                      ? darkMode
                        ? 'bg-green-600 text-white hover:bg-green-700 border border-green-500'
                        : 'bg-green-600 text-white hover:bg-green-700 border border-green-500'
                      : darkMode
                        ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:bg-gray-700'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className={`rounded-lg p-4 space-y-3 ${
            darkMode ? 'bg-gray-800' : 'bg-gray-50'
          }`}>
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Período (Opcional)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Data Início
                </label>
                <input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  disabled={disabled}
                  className={`block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white disabled:bg-gray-900 disabled:cursor-not-allowed'
                      : 'border border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Data Fim
                </label>
                <input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  disabled={disabled}
                  className={`block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white disabled:bg-gray-900 disabled:cursor-not-allowed'
                      : 'border border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed'
                  }`}
                />
              </div>
            </div>
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Deixe em branco para acesso sem limite de data
            </p>
          </div>
        </>
      )}
    </div>
  );
};
